/**
 * PRODUCT-SPECIFICATIONS MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * The product specification sheet: every SKU (from System Settings → Product
 * SKUs) with its full spec — the fixed spreadsheet columns plus any custom
 * columns the customer added — in one grid with sortable, resizable columns and
 * an Active toggle (kept in lock-step with System Settings → Product SKUs). Add
 * a product, edit any product's full spec (the SHARED ProductSpecModal), and
 * export a chosen set of SKUs to a branded Excel (.xlsx) or PDF (a portrait
 * card per SKU for a small selection, otherwise the landscape sheet).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import HomeButton from "../../../shared/components/HomeButton.jsx";
import ProductSpecModal from "../../../shared/components/ProductSpecModal.jsx";
import ExportSpecsDialog from "../components/ExportSpecsDialog.jsx";
import { listProducts, updateProduct } from "../../../shared/utils/referenceDataService.js";
import { listProductSpecs, listSpecColumns, listUpcImagesByProduct } from "../../../shared/utils/productSpecsService.js";
import { getMyPrimaryAccount, setSpecUnitSystem } from "../../../shared/utils/accountService.js";
import { SPEC_FIELDS, UNIT_SYSTEMS, fieldLabel } from "../../../shared/utils/productSpecFields.js";
import { exportProductSpecs } from "../utils/specExport.js";
import { exportProductSpecsPdf } from "../utils/specPdf.js";
import "./ProductSpecifications.css";

const MIN_COL_W = 48;

// Backlog 194: the exporters never substitute the Brand Command mark into a
// customer's document, so an export with no usable tenant logo is UNBRANDED.
// Say so in the export dialog — the user is about to send this to a buyer.
const LOGO_NOTICE = {
  none: "Exported without a logo — your company hasn't uploaded one yet. Add it in System Settings so your spec sheets carry your brand.",
  failed: "Exported without a logo — your company logo could not be loaded. Everything else in the document is complete.",
};

const alignOf = (c) => (c.kind === "num" ? "right" : c.kind === "active" || c.kind === "img" ? "center" : "left");

// Measure header text once (bold 13px, matching .psg-th) so each column loads
// wide enough to show its full heading on a single line.
let _measureCtx = null;
const _measureCache = new Map();
function headerTextW(label) {
  const key = String(label ?? "");
  if (_measureCache.has(key)) return _measureCache.get(key);
  let w;
  if (typeof document !== "undefined") {
    if (!_measureCtx) {
      _measureCtx = document.createElement("canvas").getContext("2d");
      _measureCtx.font = "700 13px Inter, system-ui, sans-serif";
    }
    w = _measureCtx.measureText(key).width;
  } else {
    w = key.length * 7.5;
  }
  _measureCache.set(key, w);
  return w;
}

// Default column width = max(kind floor, fitted header width). The padding
// covers the cell's L/R padding (10 + 20), the sort icon, and a small buffer.
const MIN_BY_KIND = { num: 72, active: 64, img: 120, dim: 84 };
const defaultW = (c) => {
  const sortable = c.kind !== "img";
  const pad = 30 + (sortable ? 18 : 0) + 6;
  const fit = Math.ceil(headerTextW(c.label) + pad);
  const floor = c.key === "__name" ? 200 : (MIN_BY_KIND[c.kind] ?? 90);
  return Math.max(floor, fit);
};

export default function ProductSpecifications() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [specsById, setSpecsById] = useState({});   // product_id -> spec row
  const [columns, setColumns] = useState([]);        // custom column defs
  const [upcImages, setUpcImages] = useState({});    // product_id -> { tag: signedUrl }
  const [unitSystem, setUnitSystem] = useState("us");
  const [accountId, setAccountId] = useState(null);
  const [accountRole, setAccountRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportNotice, setExportNotice] = useState(null);   // one-line post-export warning (see LOGO_NOTICE)
  const [modal, setModal] = useState(null);          // { product } | null  (null product = add)

  // Sort + column-resize state (same pattern as DepletionsGrid).
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [colWidths, setColWidths] = useState({});
  const dragRef = useRef(null);

  async function load() {
    setErr(null);
    try {
      const [prods, specs, cols, upc, acct] = await Promise.all([
        listProducts(), listProductSpecs(), listSpecColumns(), listUpcImagesByProduct(), getMyPrimaryAccount(),
      ]);
      setProducts(prods);
      setSpecsById(Object.fromEntries(specs.map((s) => [s.product_id, s])));
      setColumns(cols);
      setUpcImages(upc);
      if (acct) { setUnitSystem(acct.specUnitSystem); setAccountId(acct.accountId); setAccountRole(acct.role); }
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const canEditUnits = accountRole === "owner" || accountRole === "admin";
  async function changeUnitSystem(u) {
    if (u === unitSystem || !accountId) return;
    const prev = unitSystem;
    setUnitSystem(u);
    setErr(null);
    try { await setSpecUnitSystem(accountId, u); }
    catch (e) { setUnitSystem(prev); setErr(e.message); }
  }

  // Optimistic Active toggle — mirrors the checkbox in System Settings → Product SKUs.
  async function handleToggleActive(productId, checked) {
    setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, active: checked } : p)));
    try { await updateProduct(productId, { active: checked }); }
    catch (e) {
      setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, active: !checked } : p)));
      setErr(e.message || "Could not update Active.");
    }
  }

  const handleSort = useCallback((key) => {
    setSortKey((prev) => {
      if (prev === key) { setSortDir((d) => (d === "asc" ? "desc" : "asc")); return prev; }
      setSortDir("asc");
      return key;
    });
  }, []);

  const getW = useCallback((key, def) => colWidths[key] ?? def, [colWidths]);
  const startResize = useCallback((key, def, e) => {
    e.preventDefault();
    dragRef.current = { key, startX: e.clientX, startW: colWidths[key] ?? def };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const { key, startX, startW } = dragRef.current;
      setColWidths((prev) => ({ ...prev, [key]: Math.max(MIN_COL_W, startW + ev.clientX - startX) }));
    };
    const onUp = () => { dragRef.current = null; document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [colWidths]);

  // The export/grid column set: SKU + fixed spec fields (units follow the
  // sheet-wide system) + a UPC-photo column beside each UPC number + custom
  // columns + a far-right Active flag.
  const exportColumns = useMemo(() => {
    const cols = [{ label: "Product SKU", key: "__name", kind: "dim", width: 26 }];
    SPEC_FIELDS.forEach((f) => {
      cols.push({ label: fieldLabel(f, unitSystem), key: f.key, kind: f.type === "text" ? "dim" : "num" });
      if (f.key === "product_upc") cols.push({ label: "Product UPC Photo", key: "__img_product_upc", kind: "img", width: 22 });
      if (f.key === "shipping_case_upc") cols.push({ label: "Shipping Case UPC Photo", key: "__img_case_upc", kind: "img", width: 22 });
    });
    columns.forEach((c) => cols.push({ label: c.label, key: `custom__${c.key}`, kind: c.data_type === "number" ? "num" : "dim" }));
    cols.push({ label: "Active", key: "__active", kind: "active", width: 12 });
    return cols;
  }, [columns, unitSystem]);

  const rows = useMemo(() => products.map((p) => {
    const s = specsById[p.id];
    const row = { __id: p.id, __name: p.name, __active: p.active };
    SPEC_FIELDS.forEach((f) => { row[f.key] = s?.[f.key] ?? ""; });
    columns.forEach((c) => { row[`custom__${c.key}`] = s?.custom?.[c.key] ?? ""; });
    row.__img_product_upc = upcImages[p.id]?.["product-upc"] ?? "";
    row.__img_case_upc = upcImages[p.id]?.["shipping-case-upc"] ?? "";
    return row;
  }), [products, specsById, columns, upcImages]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = exportColumns.find((c) => c.key === sortKey);
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (col?.kind === "num") {
        const av = a[sortKey] === "" || a[sortKey] == null ? -Infinity : Number(a[sortKey]);
        const bv = b[sortKey] === "" || b[sortKey] == null ? -Infinity : Number(b[sortKey]);
        return (av - bv) * dir;
      }
      if (col?.kind === "active") return ((a.__active ? 1 : 0) - (b.__active ? 1 : 0)) * dir;
      return String(a[sortKey] ?? "").toLowerCase().localeCompare(String(b[sortKey] ?? "").toLowerCase()) * dir;
    });
  }, [rows, sortKey, sortDir, exportColumns]);

  const sortIndicator = (key) =>
    sortKey !== key
      ? <span className="psg-sort-icon psg-sort-icon-idle">↕</span>
      : <span className="psg-sort-icon">{sortDir === "asc" ? "↑" : "↓"}</span>;

  async function handleExport(chosenIds, format) {
    setExporting(true); setErr(null); setExportNotice(null);
    try {
      const idSet = new Set(chosenIds);
      const chosen = rows.filter((r) => idSet.has(r.__id));
      const res = format === "pdf"
        ? await exportProductSpecsPdf({ columns: exportColumns, rows: chosen })
        : await exportProductSpecs({ columns: exportColumns, rows: chosen });
      // The file has already downloaded. If it went out unbranded, hold the
      // dialog open on a one-line warning instead of closing silently.
      const notice = LOGO_NOTICE[res?.logo];
      if (notice) setExportNotice(notice);
      else setExportOpen(false);
    } catch (e) {
      setErr(e.message || "Could not export.");
    } finally {
      setExporting(false);
    }
  }

  const renderCell = (c, row) => {
    if (c.kind === "img") {
      return row[c.key]
        ? <img src={row[c.key]} alt="UPC" style={{ height: 30, maxWidth: 96, objectFit: "contain", borderRadius: 3, border: "1px solid var(--border)", verticalAlign: "middle" }} />
        : <span className="muted">—</span>;
    }
    if (c.kind === "active") {
      return <input type="checkbox" checked={!!row.__active} onChange={(e) => handleToggleActive(row.__id, e.target.checked)} title={row.__active ? "Active" : "Inactive"} />;
    }
    return row[c.key] === "" || row[c.key] == null ? <span className="muted">—</span> : String(row[c.key]);
  };

  return (
    <section className="settings-card wide">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <HomeButton onClick={() => navigate("/")} />
          <h1 style={{ margin: 0, fontSize: 22 }}>Product Specifications</h1>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className="ps-unit-toggle" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            title="Switches every dimension, shipping-case and pallet column between US (lbs/in) and metric (kg/cm) across the whole sheet.">
            <span className="muted" style={{ fontSize: 12 }}>Units</span>
            {UNIT_SYSTEMS.map((u) => (
              <button key={u} type="button"
                className={unitSystem === u ? "btn" : "btn secondary"}
                disabled={loading || !canEditUnits || unitSystem === u}
                onClick={() => changeUnitSystem(u)}
                style={{ padding: "2px 10px", fontSize: 12 }}>
                {u === "us" ? "US (lbs/in)" : "Metric (kg/cm)"}
              </button>
            ))}
          </span>
          <button type="button" className="secondary" disabled={exporting || loading || !rows.length} onClick={() => { setExportNotice(null); setExportOpen(true); }}>
            {exporting ? "Exporting…" : "Export"}
          </button>
          <button className="btn" type="button" onClick={() => setModal({ product: null })}>+ Add Product</button>
        </div>
      </div>

      <p className="muted" style={{ marginTop: 8 }}>
        Every SKU and its full specification. Click a column heading to sort; drag
        a heading's right edge to resize. Editing a product here also updates it in
        System Settings → Product SKUs.
      </p>

      {err && <p className="error">{err}</p>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : products.length === 0 ? (
        <p className="muted">No products yet — click <strong>Add Product</strong> to create your first one.</p>
      ) : (
        <div className="psg-scroll">
          <table className="psg-table">
            <thead>
              <tr>
                {exportColumns.map((c) => {
                  const w = getW(c.key, defaultW(c));
                  const sortable = c.kind !== "img";
                  return (
                    <th
                      key={c.key}
                      className={`psg-th${c.key === "__name" ? " psg-sticky" : ""}${sortable ? " psg-th-sortable" : ""}`}
                      style={{ width: w, minWidth: w, maxWidth: w, left: c.key === "__name" ? 0 : undefined, textAlign: alignOf(c) }}
                      onClick={sortable ? () => handleSort(c.key) : undefined}
                    >
                      {c.label}{sortable && sortIndicator(c.key)}
                      <span className="psg-resize" onMouseDown={(e) => { e.stopPropagation(); startResize(c.key, defaultW(c), e); }} />
                    </th>
                  );
                })}
                <th className="psg-th" style={{ width: 90, minWidth: 90, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr key={row.__id}>
                  {exportColumns.map((c) => {
                    const w = getW(c.key, defaultW(c));
                    return (
                      <td
                        key={c.key}
                        className={`psg-td${c.key === "__name" ? " psg-sticky" : ""}`}
                        style={{ width: w, minWidth: w, maxWidth: w, left: c.key === "__name" ? 0 : undefined, textAlign: alignOf(c), fontWeight: c.key === "__name" ? 600 : 400 }}
                      >
                        {renderCell(c, row)}
                      </td>
                    );
                  })}
                  <td className="psg-td" style={{ textAlign: "right" }}>
                    <button type="button" className="link" onClick={() => setModal({ product: products.find((p) => p.id === row.__id) })}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ProductSpecModal
          product={modal.product}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}

      {exportOpen && (
        <ExportSpecsDialog
          products={products.map((p) => ({ id: p.id, name: p.name, active: p.active }))}
          busy={exporting}
          notice={exportNotice}
          onExport={handleExport}
          onClose={() => { setExportNotice(null); setExportOpen(false); }}
        />
      )}
    </section>
  );
}
